import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { portalFetch } from '../lib/supabase';
import GoogleReviews from './GoogleReviews';

function fmtDate(iso) {
  if (!iso) return '–';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function Stars({ rating }) {
  return (
    <span style={{ color: '#F59E0B', letterSpacing: 1 }}>
      {'★'.repeat(rating)}{'☆'.repeat(5 - rating)}
    </span>
  );
}

function Card({ children, style, onClick }) {
  return <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 12, ...style }} onClick={onClick}>{children}</div>;
}

const SENTIMENT_COLORS = {
  positive: { bg: '#EAF3DE', tx: '#27500A', label: 'Positive' },
  mixed:    { bg: '#FAEEDA', tx: '#633806', label: 'Mixed' },
  negative: { bg: '#FCEBEB', tx: '#791F1F', label: 'Negative' },
};

function SentimentBadge({ sentiment }) {
  const s = SENTIMENT_COLORS[sentiment] || SENTIMENT_COLORS.mixed;
  return (
    <span style={{ background: s.bg, color: s.tx, padding: '0.2rem 0.65rem', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700 }}>
      {s.label}
    </span>
  );
}

function TableChatReviews() {
  const { restaurant } = useAuth();
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState(null);
  const [filtered, setFiltered] = useState([]);

  const load = async (sentimentFilter = null) => {
    if (!restaurant?.id) return;
    setLoading(true);
    const path   = sentimentFilter ? `/reviews?sentiment=${sentimentFilter}` : '/reviews';
    const result = await portalFetch(path);
    if (result) {
      setData(result);
      setFiltered(result.reviews || []);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [restaurant?.id]);

  const handleFilterClick = (sentiment) => {
    if (filter === sentiment) { setFilter(null); load(null); }
    else { setFilter(sentiment); load(sentiment); }
  };

  // Response drafting -- on-demand per review, not automatic. The
  // backend (api/portal/generate-response.js) refuses outright for a
  // "critical" severity review -- that's enforced server-side, not just
  // hidden client-side, so this UI's job is just to reflect that state,
  // not to be the actual safety check.
  const [generatingIds, setGeneratingIds] = useState(new Set());
  const [copiedIds, setCopiedIds]         = useState(new Set());

  const updateReviewDraft = (reviewId, draftResponse) => {
    const apply = list => list.map(r => r.id === reviewId ? { ...r, draft_response: draftResponse } : r);
    setFiltered(prev => apply(prev));
    setData(prev => prev ? { ...prev, reviews: apply(prev.reviews || []) } : prev);
  };

  const handleGenerateResponse = async (reviewId) => {
    setGeneratingIds(prev => new Set(prev).add(reviewId));
    const result = await portalFetch('/generate-response', {
      method: 'POST',
      body: JSON.stringify({ reviewId }),
    });
    if (result?.draftResponse) {
      // Regenerating replaces the wording, so the server also unpublishes it.
      updateReviewPublished(reviewId, null);
      updateReviewDraft(reviewId, result.draftResponse);
      setEditTexts(prev => { const n = { ...prev }; delete n[reviewId]; return n; });
    }
    setGeneratingIds(prev => { const next = new Set(prev); next.delete(reviewId); return next; });
  };

  // Editable drafts: unsaved edits live here until the owner saves them.
  const [editTexts, setEditTexts] = useState({});
  const [savingIds, setSavingIds] = useState(new Set());

  const handleSaveResponse = async (reviewId) => {
    const text = editTexts[reviewId];
    if (text === undefined) return;
    setSavingIds(prev => new Set(prev).add(reviewId));
    const result = await portalFetch('/save-response', {
      method: 'POST',
      body: JSON.stringify({ reviewId, source: 'dish', draftResponse: text }),
    });
    if (typeof result?.draftResponse === 'string') {
      updateReviewDraft(reviewId, result.draftResponse);
      setEditTexts(prev => { const n = { ...prev }; delete n[reviewId]; return n; });
    }
    setSavingIds(prev => { const n = new Set(prev); n.delete(reviewId); return n; });
  };

  const [publishingIds, setPublishingIds] = useState(new Set());

  const updateReviewPublished = (reviewId, publishedAt) => {
    const apply = list => list.map(r => r.id === reviewId ? { ...r, response_published_at: publishedAt } : r);
    setFiltered(prev => apply(prev));
    setData(prev => prev ? { ...prev, reviews: apply(prev.reviews || []) } : prev);
  };

  const handlePublish = async (reviewId, publish) => {
    setPublishingIds(prev => new Set(prev).add(reviewId));
    const result = await portalFetch('/publish-response', {
      method: 'POST',
      body: JSON.stringify({ reviewId, publish }),
    });
    if (result && 'responsePublishedAt' in result) {
      updateReviewPublished(reviewId, result.responsePublishedAt);
    }
    setPublishingIds(prev => { const n = new Set(prev); n.delete(reviewId); return n; });
  };

  const handleCopyResponse = (reviewId, text) => {
    navigator.clipboard.writeText(text);
    setCopiedIds(prev => new Set(prev).add(reviewId));
    setTimeout(() => {
      setCopiedIds(prev => { const next = new Set(prev); next.delete(reviewId); return next; });
    }, 1800);
  };

  const agg = data?.aggregate || { total: 0, positive: 0, mixed: 0, negative: 0, avgRating: null };

  return (
    <div>
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ fontFamily: 'Fraunces, serif', fontSize: '1.8rem', fontWeight: 900 }}>Dish Reviews</h1>
        <p style={{ color: 'var(--muted)', fontSize: '0.875rem', marginTop: 2 }}>
          Customer reviews of dishes at {restaurant?.name}
        </p>
      </div>

      {loading ? (
        <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--muted)' }}>Loading reviews...</div>
      ) : (
        <>
          {/* Aggregate stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
            <Card style={{ padding: '1.2rem 1.4rem' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.4rem' }}>Total reviews</div>
              <div style={{ fontFamily: 'Fraunces, serif', fontSize: '1.7rem', fontWeight: 900, color: 'var(--orange)' }}>{agg.total}</div>
            </Card>
            <Card style={{ padding: '1.2rem 1.4rem' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.4rem' }}>Avg dish rating</div>
              <div style={{ fontFamily: 'Fraunces, serif', fontSize: '1.7rem', fontWeight: 900, color: 'var(--orange)' }}>
                {agg.avgRating ? `★${agg.avgRating}` : '–'}
              </div>
            </Card>

            {/* Sentiment counts — clickable for all tiers */}
            {[
              { key: 'positive', label: 'Positive', count: agg.positive, color: '#27500A', bg: '#EAF3DE' },
              { key: 'mixed',    label: 'Mixed',    count: agg.mixed,    color: '#633806', bg: '#FAEEDA' },
              { key: 'negative', label: 'Negative', count: agg.negative, color: '#791F1F', bg: '#FCEBEB' },
            ].map(({ key, label, count, color, bg }) => (
              <Card
                key={key}
                style={{
                  padding: '1.2rem 1.4rem',
                  cursor: 'pointer',
                  border: filter === key ? `2px solid ${color}` : '1px solid var(--border)',
                  transition: 'border 0.15s',
                }}
                onClick={() => handleFilterClick(key)}
              >
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.4rem' }}>
                  {label}
                  <span style={{ marginLeft: 6, fontSize: '0.65rem', background: bg, color, padding: '0.1rem 0.4rem', borderRadius: 10 }}>
                    {filter === key ? 'Viewing' : 'Click to filter'}
                  </span>
                </div>
                <div style={{ fontFamily: 'Fraunces, serif', fontSize: '1.7rem', fontWeight: 900, color }}>{count}</div>
              </Card>
            ))}
          </div>

          {/* Full reviews — available to all tiers */}
          {filter && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', marginBottom: '1rem' }}>
              <span style={{ fontSize: '0.875rem', color: 'var(--muted)' }}>
                Showing {filter} reviews ({filtered.length})
              </span>
              <button
                onClick={() => { setFilter(null); load(null); }}
                style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, padding: '0.3rem 0.8rem', fontSize: '0.8rem', cursor: 'pointer', color: 'var(--mid)' }}
              >
                Clear filter
              </button>
            </div>
          )}

          <Card>
            {filtered.length === 0 ? (
              <div style={{ padding: '3rem', textAlign: 'center' }}>
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem', opacity: 0.4 }}>⭐</div>
                <p style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>
                  {agg.total === 0 ? 'No reviews yet. Reviews appear 24 hours after orders.' : `No ${filter} reviews found.`}
                </p>
              </div>
            ) : (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 80px 120px 1fr', gap: '1rem', padding: '0.75rem 1.4rem', borderBottom: '1px solid var(--border)', background: 'var(--warm)', borderRadius: '12px 12px 0 0' }}>
                  {['Review', 'Dish', 'Rating', 'Sentiment', 'Date'].map(h => (
                    <div key={h} style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{h}</div>
                  ))}
                </div>
                {filtered.map(review => (
                  <div
                    key={review.id}
                    style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 80px 120px 1fr', gap: '1rem', padding: '1rem 1.4rem', borderBottom: '1px solid var(--warm)', alignItems: 'start' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--cream)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'white'}
                  >
                    <div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--ink)', lineHeight: 1.5, marginBottom: '0.3rem' }}>
                        {review.review_text}
                      </div>
                      {review.sentiment_summary && (
                        <div style={{ fontSize: '0.78rem', color: 'var(--muted)', fontStyle: 'italic' }}>
                          {review.sentiment_summary}
                        </div>
                      )}
                      {review.keywords?.length > 0 && (
                        <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', marginTop: '0.4rem' }}>
                          {review.keywords.map((k, i) => (
                            <span key={i} style={{ fontSize: '0.7rem', background: 'var(--warm)', border: '1px solid var(--border)', color: 'var(--mid)', padding: '0.1rem 0.5rem', borderRadius: 20 }}>
                              {k}
                            </span>
                          ))}
                        </div>
                      )}
                      {review.customer_name && (
                        <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: '0.3rem' }}>
                          — {review.customer_name}
                        </div>
                      )}
                    </div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--mid)', paddingTop: '0.1rem' }}>{review.dish_name}</div>
                    <div style={{ paddingTop: '0.1rem' }}><Stars rating={review.rating} /></div>
                    <div style={{ paddingTop: '0.1rem' }}><SentimentBadge sentiment={review.sentiment} /></div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--muted)', paddingTop: '0.1rem' }}>{fmtDate(review.created_at)}</div>

                    <div style={{ gridColumn: '1 / -1', marginTop: '0.6rem', paddingTop: '0.6rem', borderTop: '1px solid var(--warm)' }}>
                      {review.severity === 'critical' ? (
                        <div style={{ fontSize: '0.8rem', color: '#791F1F', background: '#FCEBEB', padding: '0.5rem 0.8rem', borderRadius: 8, display: 'inline-block' }}>
                          ⚠️ This review needs a personal response, not an AI-drafted one.
                        </div>
                      ) : review.draft_response ? (
                        <div>
                          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.3rem' }}>
                            Draft response (editable){' '}
                            {review.response_published_at ? (
                              <span style={{ marginLeft: 6, fontSize: '0.65rem', background: '#EAF3DE', color: '#27500A', padding: '0.1rem 0.5rem', borderRadius: 10, textTransform: 'none', letterSpacing: 0 }}>Published on your website</span>
                            ) : (
                              <span style={{ marginLeft: 6, fontSize: '0.65rem', background: 'var(--warm)', color: 'var(--mid)', padding: '0.1rem 0.5rem', borderRadius: 10, textTransform: 'none', letterSpacing: 0, border: '1px solid var(--border)' }}>Not published</span>
                            )}
                          </div>
                          <div style={{ fontSize: '0.85rem', color: 'var(--ink)', lineHeight: 1.5, background: 'var(--warm)', padding: '0.7rem 0.9rem', borderRadius: 8, marginBottom: '0.5rem' }}>
                            <textarea
                              value={editTexts[review.id] ?? review.draft_response}
                              onChange={e => setEditTexts(prev => ({ ...prev, [review.id]: e.target.value }))}
                              rows={4}
                              maxLength={1000}
                              aria-label="Edit draft response"
                              style={{ width: '100%', boxSizing: 'border-box', border: 'none', background: 'transparent', resize: 'vertical', font: 'inherit', color: 'inherit', lineHeight: 'inherit', outline: 'none' }}
                            />
                          </div>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            {review.draft_response && (
                              <button
                                onClick={() => handlePublish(review.id, !review.response_published_at)}
                                disabled={publishingIds.has(review.id) || (editTexts[review.id] !== undefined && editTexts[review.id] !== review.draft_response)}
                                title={editTexts[review.id] !== undefined && editTexts[review.id] !== review.draft_response ? 'Save your edits first' : ''}
                                style={{ background: review.response_published_at ? 'none' : '#27500A', color: review.response_published_at ? 'var(--mid)' : 'white', border: review.response_published_at ? '1px solid var(--border)' : 'none', borderRadius: 6, padding: '0.3rem 0.8rem', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', opacity: (publishingIds.has(review.id) || (editTexts[review.id] !== undefined && editTexts[review.id] !== review.draft_response)) ? 0.5 : 1 }}
                              >
                                {publishingIds.has(review.id) ? 'Working...' : review.response_published_at ? 'Unpublish' : 'Publish on website'}
                              </button>
                            )}
                            {editTexts[review.id] !== undefined && editTexts[review.id] !== review.draft_response && (
                              <button
                                onClick={() => handleSaveResponse(review.id)}
                                disabled={savingIds.has(review.id)}
                                style={{ background: 'var(--brand, #E85D04)', color: 'white', border: 'none', borderRadius: 6, padding: '0.3rem 0.8rem', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', opacity: savingIds.has(review.id) ? 0.6 : 1 }}
                              >
                                {savingIds.has(review.id) ? 'Saving...' : 'Save edits'}
                              </button>
                            )}
                            <button
                              onClick={() => handleCopyResponse(review.id, editTexts[review.id] ?? review.draft_response)}
                              style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, padding: '0.3rem 0.8rem', fontSize: '0.78rem', cursor: 'pointer', color: 'var(--mid)' }}
                            >
                              {copiedIds.has(review.id) ? 'Copied!' : 'Copy'}
                            </button>
                            <button
                              onClick={() => handleGenerateResponse(review.id)}
                              disabled={generatingIds.has(review.id)}
                              style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, padding: '0.3rem 0.8rem', fontSize: '0.78rem', cursor: 'pointer', color: 'var(--mid)', opacity: generatingIds.has(review.id) ? 0.6 : 1 }}
                            >
                              {generatingIds.has(review.id) ? 'Regenerating...' : 'Regenerate'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleGenerateResponse(review.id)}
                          disabled={generatingIds.has(review.id)}
                          style={{ background: 'var(--brand, #E85D04)', color: 'white', border: 'none', borderRadius: 6, padding: '0.4rem 0.9rem', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', opacity: generatingIds.has(review.id) ? 0.6 : 1 }}
                        >
                          {generatingIds.has(review.id) ? 'Generating...' : 'Generate response'}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

// Tab wrapper: TableChat verified reviews (the original screen) and Google reviews.
export default function Reviews() {
  const [tab, setTab] = useState(() => (new URLSearchParams(window.location.search).get('google') ? 'google' : 'tablechat'));
  const tabStyle = (active) => ({
    background: active ? 'var(--brand, #E85D04)' : 'white',
    color: active ? 'white' : 'var(--mid)',
    border: '1px solid var(--border)',
    borderRadius: 20,
    padding: '0.4rem 1rem',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
  });
  return (
    <div>
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.2rem' }}>
        <button style={tabStyle(tab === 'tablechat')} onClick={() => setTab('tablechat')}>TableChat reviews</button>
        <button style={tabStyle(tab === 'google')} onClick={() => setTab('google')}>Google reviews</button>
      </div>
      {tab === 'google' ? <GoogleReviews /> : <TableChatReviews />}
    </div>
  );
}